import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ArrowLeft, ChevronRight, Shield, Fuel, Activity, Wrench, Monitor, Leaf,
  AlertTriangle, TrendingDown, Clock, DollarSign, CheckCircle2, XCircle,
  BarChart3, Target, Phone, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

// IMPL-034 (Fase 1c, 2026-06-29) · Tema CLARO unificado con Landing/Calculadora
// El color de cada área se mantiene como acento (badge, iconos, CTA, sección final) sobre fondo blanco.
const areaConfig = {
  seguridad: {
    icono: Shield,
    color: 'from-red-500 to-rose-600',
    colorLight: 'red',
    colorBg: 'bg-red-500',
    accentText: 'text-red-600',
    accentBg: 'bg-red-50',
    accentBorder: 'border-red-200',
    apiId: 'area_seguridad'
  },
  combustible: {
    icono: Fuel,
    color: 'from-amber-500 to-orange-600',
    colorLight: 'amber',
    colorBg: 'bg-amber-500',
    accentText: 'text-amber-700',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-200',
    apiId: 'area_combustible'
  },
  operativa: {
    icono: Activity,
    color: 'from-blue-500 to-indigo-600',
    colorLight: 'blue',
    colorBg: 'bg-blue-500',
    accentText: 'text-blue-600',
    accentBg: 'bg-blue-50',
    accentBorder: 'border-blue-200',
    apiId: 'area_operativa'
  },
  mantenimiento: {
    icono: Wrench,
    color: 'from-purple-500 to-violet-600',
    colorLight: 'purple',
    colorBg: 'bg-purple-500',
    accentText: 'text-purple-600',
    accentBg: 'bg-purple-50',
    accentBorder: 'border-purple-200',
    apiId: 'area_mantenimiento'
  },
  tic: {
    icono: Monitor,
    color: 'from-cyan-500 to-teal-600',
    colorLight: 'cyan',
    colorBg: 'bg-cyan-500',
    accentText: 'text-cyan-700',
    accentBg: 'bg-cyan-50',
    accentBorder: 'border-cyan-200',
    apiId: 'area_tic'
  },
  sostenibilidad: {
    icono: Leaf,
    color: 'from-emerald-500 to-green-600',
    colorLight: 'emerald',
    colorBg: 'bg-emerald-500',
    accentText: 'text-emerald-600',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-200',
    apiId: 'area_sostenibilidad'
  }
};

// Iconos para los problemas
const problemaIconos = [AlertTriangle, TrendingDown, XCircle, DollarSign];

// Logo unificado (mismo patrón que Landing.jsx y Calculadora)
const MobilsafeLogoArea = () => (
  <div className="flex items-center gap-3">
    <img
      src={MOBILSAFE_LOGO}
      alt="MOBILSAFE"
      className="h-9 md:h-10"
      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
    />
    <div className="hidden items-center gap-2">
      <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow">
        <Leaf className="w-5 h-5 text-white" />
      </div>
      <div>
        <h1 className="text-lg font-black tracking-tight text-slate-900">MOBILSAFE</h1>
        <p className="text-[9px] text-emerald-600 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
      </div>
    </div>
  </div>
);

const AreaLanding = ({ areaId, onBack, onSimular }) => {
  const [textos, setTextos] = useState(null);
  const [loading, setLoading] = useState(true);

  const config = areaConfig[areaId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [areaId]);

  useEffect(() => {
    const fetchTextos = async () => {
      if (!config) return;
      try {
        const response = await axios.get(`${API}/landing-textos/${config.apiId}`);
        setTextos(response.data);
      } catch (error) {
        console.error('Error cargando textos:', error);
      }
      setLoading(false);
    };
    fetchTextos();
  }, [areaId, config]);

  if (!config) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Área no encontrada</h1>
          <button onClick={onBack} className="px-6 py-3 bg-emerald-500 text-white rounded-lg font-bold hover:bg-emerald-600">Volver</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Extraer datos de las secciones
  const getSeccion = (id) => textos?.secciones?.find(s => s.id === id)?.campos || {};
  const hero = getSeccion('hero');
  const stats = getSeccion('stats');
  const problemas = getSeccion('problemas');
  const soluciones = getSeccion('soluciones');
  const ctaFinal = getSeccion('cta_final');

  const IconComponent = config.icono;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-lg transition-all text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <MobilsafeLogoArea />
          </div>
          <button
            onClick={onSimular}
            className={`px-6 py-2 bg-gradient-to-r ${config.color} rounded-lg font-semibold text-sm text-white hover:opacity-90 transition-all shadow`}
            data-testid="simular-btn"
          >
            Simular mi flota
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-[0.04]`} />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className={`inline-flex items-center gap-2 ${config.accentBg} border ${config.accentBorder} px-4 py-2 rounded-full`}>
                <IconComponent className={`w-5 h-5 ${config.accentText}`} />
                <span className={`text-sm font-semibold uppercase tracking-wide ${config.accentText}`}>{hero.badge || areaId}</span>
              </div>

              <h1 className="text-4xl lg:text-5xl font-black leading-tight text-slate-900">
                {hero.titulo || 'Cargando...'}
              </h1>

              <p className="text-xl text-slate-700 leading-relaxed">
                {hero.subtitulo}
              </p>

              <p className="text-lg text-slate-500">
                {hero.descripcion}
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <button
                  onClick={onSimular}
                  className={`group px-8 py-4 bg-gradient-to-r ${config.color} rounded-xl font-bold text-white shadow-xl transition-all transform hover:scale-105 flex items-center gap-3`}
                  data-testid="hero-cta"
                >
                  <BarChart3 className="w-5 h-5" />
                  {hero.boton_cta || 'Calcular impacto'}
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((num) => (
                <div
                  key={num}
                  className="bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm"
                >
                  <p className={`text-3xl font-black ${config.accentText}`}>
                    {stats[`stat_${num}_valor`] || '-'}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">{stats[`stat_${num}_texto`] || ''}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problemas */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black mb-4 text-slate-900">{problemas.titulo_seccion || '¿Te suena alguno de estos problemas?'}</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {problemas.subtitulo_seccion || 'Son los retos más comunes que encontramos'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((num, idx) => {
              const ProblemaIcon = problemaIconos[idx];
              const titulo = problemas[`problema_${num}_titulo`];
              const desc = problemas[`problema_${num}_desc`];
              if (!titulo) return null;
              return (
                <div
                  key={num}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-red-300 hover:shadow-md transition-all"
                >
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-red-50 border border-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <ProblemaIcon className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2 text-slate-900">{titulo}</h3>
                      <p className="text-slate-600 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pregunta clave */}
          {problemas.pregunta_clave && (
            <div className={`mt-12 bg-gradient-to-r ${config.color} rounded-2xl p-8 text-center shadow-xl`}>
              <p className="text-2xl font-bold text-white">{problemas.pregunta_clave}</p>
            </div>
          )}
        </div>
      </section>

      {/* Soluciones */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black mb-4 text-slate-900">{soluciones.titulo_seccion || 'Cómo lo solucionamos con MOBILSAFE'}</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {soluciones.subtitulo_seccion || 'Tecnología probada en más de 500 flotas'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5].map((num) => {
              const solucion = soluciones[`solucion_${num}`];
              if (!solucion) return null;
              return (
                <div
                  key={num}
                  className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-3 shadow-sm hover:border-emerald-300 hover:shadow-md transition-all"
                >
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 leading-relaxed">{solucion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className={`py-20 bg-gradient-to-r ${config.color}`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-black mb-6 text-white">
            {ctaFinal.titulo || '¿Quieres saber cuánto podrías mejorar?'}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {ctaFinal.descripcion || 'Simula el impacto de MOBILSAFE en tu flota en menos de 2 minutos'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={onSimular}
              className="px-8 py-4 bg-white text-slate-900 rounded-xl font-bold text-lg shadow-xl hover:bg-slate-100 transition-all flex items-center gap-3"
              data-testid="final-cta"
            >
              <Target className="w-5 h-5" />
              {ctaFinal.boton_principal || 'Simular ahora'}
              <ChevronRight className="w-5 h-5" />
            </button>
            <a
              href="tel:+34900000000"
              className="px-8 py-4 bg-white/20 backdrop-blur rounded-xl font-bold text-lg text-white hover:bg-white/30 transition-all flex items-center gap-3"
            >
              <Phone className="w-5 h-5" />
              {ctaFinal.boton_secundario || 'Hablar con un experto'}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img
            src={MOBILSAFE_LOGO}
            alt="MOBILSAFE"
            className="h-8 mx-auto mb-4 opacity-80"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
          <div className="hidden items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <span className="text-base font-black tracking-tight text-slate-900">MOBILSAFE</span>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <a
              href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-800 transition-all"
            >
              Condiciones de uso y privacidad
            </a>
            <span className="text-slate-300">|</span>
            <a
              href="https://www.mobilsafe.es/politica-de-cookies/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-800 transition-all"
            >
              Política de cookies
            </a>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 MOBILSAFE Fleet Technology. Comprometidos con la movilidad sostenible.
          </p>
        </div>
      </footer>
    </div>
  );
};

export { AreaLanding, areaConfig };
export default AreaLanding;
